import React, { useState } from 'react';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import Navbar from '../../components/common/Navbar';
import CreateOrder from './CreateOrder';
import OrderDetail from './OrderDetail';
import InvoicesPage from './InvoicesPage';
import ProjectsPage from './ProjectsPage';
import ProjectDetail from './ProjectDetail';
import NotificationsPage from '../common/NotificationsPage';
import FeedbackPage from '../common/FeedbackPage';
import OrdererProfile from './OrdererProfile';
import PaymentCardSection from '../../components/orderer/PaymentCardSection';
import BillingProfileNotice from '../../components/orderer/BillingProfileNotice';
import { isBillingProfileComplete } from '../../utils/billingProfile';
import OrdererTabBar, { isOrdererRootPath, useNarrow, ORDERER_TAB_BAR_PX } from '../../components/orderer/OrdererTabBar';
import OrgSetupPage from './OrgSetupPage';
import OrgPage from './OrgPage';
import { OrderSupplierLogo } from '../../components/common/SupplierLogo';
import BroadcastModal from '../../components/common/BroadcastModal';
import { useOrdererOrders } from '../../hooks/useOrdererOrders';
import { useBidCounts } from '../../hooks/useBidCounts';
import { isBidExpired } from '../../utils/orderExpiry';
import { useAuth } from '../../contexts/AuthContext';
import { useFcmToken } from '../../hooks/useFcmToken';
import { placeName } from '../../utils/orderCardFormat';
import { orderTitle } from '../../utils/orderTitle';
import { COLORS, FONTS, SHADOWS } from '../../styles/tokens';
import { TruckEmpty } from '../../components/common/truck';

// ── Status config ──────────────────────────────────────────────────────────
const STATUS_LABEL = {
  draft:             { text: 'Utkast',          bg: COLORS.surfaceHover, color: COLORS.textLight,   border: COLORS.border },
  open:              { text: 'Åpen',            bg: COLORS.blueBg,     color: COLORS.blueText,      border: COLORS.blue },
  matched:           { text: 'Tildelt',         bg: COLORS.purpleBg,   color: COLORS.purpleText,    border: COLORS.purple },
  in_progress:       { text: 'Under transport', bg: COLORS.warningBg,  color: COLORS.warningText,   border: COLORS.warning },
  delivered_pending: { text: 'Venter',          bg: COLORS.purpleBg,   color: COLORS.purpleText,    border: COLORS.purple },
  completed:         { text: 'Fullført',        bg: COLORS.successBg,  color: COLORS.successText,   border: COLORS.success },
  invoiced:          { text: 'Fakturert',       bg: COLORS.successBg,  color: COLORS.successText,   border: COLORS.success },
  disputed:          { text: 'Tvist',           bg: COLORS.dangerBg,   color: COLORS.dangerText,    border: COLORS.danger },
};

// ── Helpers ────────────────────────────────────────────────────────────────
function getGreeting(name) {
  const h = new Date().getHours();
  const salutation = h < 10 ? 'God morgen' : h < 17 ? 'Hei' : 'God kveld';
  return name ? `${salutation}, ${name.split(' ')[0]}` : salutation;
}

function formatDate() {
  const s = new Date().toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'long' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function whenLabel(date) {
  return formatDeliveryTime(date).toUpperCase();
}

function formatDeliveryTime(date) {
  const now      = new Date();
  const tomorrow = new Date(now.getTime() + 86400000);
  const time     = date.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
  if (date.toDateString() === now.toDateString())      return `I dag · ${time}`;
  if (date.toDateString() === tomorrow.toDateString()) return `I morgen · ${time}`;
  return date.toLocaleDateString('nb-NO', { weekday: 'short', day: 'numeric', month: 'short' }) + ` · ${time}`;
}

// ── Root component ─────────────────────────────────────────────────────────
export default function OrdererDashboard() {
  const { orders, loading, error } = useOrdererOrders();
  const { userDoc, currentUser, refreshUserDoc, viewingAs } = useAuth();
  useFcmToken();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const narrow = useNarrow();
  const bottomSpace = narrow && isOrdererRootPath(pathname) ? ORDERER_TAB_BAR_PX + 16 : 0;

  const [activeModal, setActiveModal] = useState(null);

  const now = new Date();

  // Orders that need immediate action
  const needsAction = orders.filter(o =>
    o.status === 'delivered_pending' ||
    (o.status === 'open' && isBidExpired(o, now))
  );

  // Antall bud på oppdrag med utløpt frist avgjør om bestiller skal velge vinner eller publisere på nytt.
  const bidCounts = useBidCounts(needsAction.filter(o => o.status === 'open').map(o => o.id));

  // In-flight orders (excluding action-required ones)
  const inFlight = orders.filter(o => {
    if (!['open', 'matched', 'in_progress'].includes(o.status)) return false;
    if (o.status === 'open' && isBidExpired(o, now)) return false;
    return true;
  });

  // Upcoming deliveries within 48h
  const in48h = new Date(now.getTime() + 48 * 60 * 60 * 1000);
  const upcomingDeliveries = orders
    .filter(o => {
      const d = o.scheduledAt?.toDate?.();
      return d && d > now && d < in48h;
    })
    .sort((a, b) => (a.scheduledAt?.toDate?.() ?? 0) - (b.scheduledAt?.toDate?.() ?? 0));

  // Leverte oppdrag (completed / invoiced), nyeste først
  // Sorteringsrekkefølge: deliveredAt → scheduledAt → createdAt (alle kan i teorien være null)
  const delivered = orders
    .filter(o => ['completed', 'invoiced'].includes(o.status))
    .sort((a, b) => {
      const aT = a.deliveredAt?.toDate?.() ?? a.scheduledAt?.toDate?.() ?? a.createdAt?.toDate?.() ?? 0;
      const bT = b.deliveredAt?.toDate?.() ?? b.scheduledAt?.toDate?.() ?? b.createdAt?.toDate?.() ?? 0;
      return bT - aT;
    });

  // Stats
  const completedCount = delivered.length;

  return (
    <div className="orderer-ui" style={{ paddingBottom: bottomSpace }}>
    <Routes>
      <Route path="create"               element={<CreateOrder />} />
      <Route path="order/:orderId"       element={<OrderDetail orders={orders} />} />
      <Route path="invoices"             element={<InvoicesPage />} />
      <Route path="projects"            element={<ProjectsPage />} />
      <Route path="projects/:projectId" element={<ProjectDetail />} />
      <Route path="varsler"             element={<NotificationsPage />} />
      <Route path="tilbakemelding"      element={<FeedbackPage />} />
      <Route path="profil"              element={<OrdererProfile />} />
      <Route path="org"                  element={<OrgPage />} />
      <Route path="org/setup"            element={<OrgSetupPage />} />
      <Route path="*" element={
        <div style={styles.root}>
          <Navbar />
          <div style={styles.page}>

            {/* ── Header ───────────────────────────────────────── */}
            <div style={styles.pageHeader}>
              <div>
                <p style={styles.dateStr}>{formatDate().toUpperCase()}</p>
                <h1 style={styles.greeting}>{getGreeting(userDoc?.name)}</h1>
              </div>
              {!narrow && (
                <button
                  className="tippi-btn"
                  style={styles.newBtn}
                  onClick={() => navigate('/orderer/create')}
                >
                  + Nytt oppdrag
                </button>
              )}
            </div>

            {/* Ikke-blokkerende påminnelse; oppdrag kan ikke opprettes før fakturaopplysningene er fylt ut */}
            {userDoc && !viewingAs && !isBillingProfileComplete(userDoc.billingProfile) && (
              <BillingProfileNotice style={{ marginBottom: 16 }}>
                Fyll ut fakturaopplysninger for å kunne opprette oppdrag.
              </BillingProfileNotice>
            )}

            {/* Kortet administreres under Profil på mobil */}
            {!narrow && (
              <PaymentCardSection
                userDoc={userDoc}
                currentUser={currentUser}
                onRefresh={refreshUserDoc}
              />
            )}

            {error && <div style={styles.errorMsg}>Feil ved henting av oppdrag</div>}

            {/* ── Stats row ────────────────────────────────────── */}
            <div style={styles.statsGrid}>
              <StatCard
                value={loading ? '—' : inFlight.length}
                label="Aktive oppdrag"
                dark={!loading && inFlight.length > 0}
                onClick={loading ? undefined : () => setActiveModal('active')}
              />
              <StatCard
                value={loading ? '—' : needsAction.length}
                label="Venter på handling"
                onClick={loading ? undefined : () => setActiveModal('action')}
              />
              <StatCard
                value={loading ? '—' : completedCount}
                label="Fullførte oppdrag"
                onClick={loading ? undefined : () => setActiveModal('completed')}
              />
            </div>

            {/* ── Needs action ─────────────────────────────────── */}
            {!loading && needsAction.length > 0 && (
              <div style={styles.actionSection}>
                <div style={styles.actionHeader}>
                  <span style={styles.actionDot} />
                  <span style={styles.actionTitle}>Trenger handling</span>
                  <span style={styles.actionCount}>{needsAction.length}</span>
                </div>
                <div style={styles.actionList}>
                  {needsAction.map(order => (
                    <ActionItem
                      key={order.id}
                      order={order}
                      bidCount={bidCounts[order.id]}
                      onCopy={() => navigate(`/orderer/create?copy=${order.id}`)}
                      onNavigate={() => navigate(`/orderer/order/${order.id}`)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* ── Active orders ─────────────────────────────────── */}
            {!loading && inFlight.length > 0 && (
              <div style={styles.section}>
                <div style={styles.sectionHeader}>
                  <span style={styles.sectionTitle}>Aktive oppdrag</span>
                  {inFlight.length > 4 && (
                    <button style={styles.seeAllBtn} onClick={() => navigate('/orderer/all')}>
                      Se alle {inFlight.length} →
                    </button>
                  )}
                </div>
                <div style={styles.rows}>
                  {inFlight.slice(0, 4).map(order => (
                    <OrderRow
                      key={order.id}
                      order={order}
                      onClick={() => navigate(`/orderer/order/${order.id}`)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* ── Upcoming deliveries ───────────────────────────── */}
            {!loading && upcomingDeliveries.length > 0 && (
              <div style={styles.section}>
                <div style={styles.sectionHeader}>
                  <span style={styles.sectionTitle}>Kommende leveringer, neste 48 timer</span>
                </div>
                <div style={styles.rows}>
                  {upcomingDeliveries.map(order => (
                    <OrderRow
                      key={order.id}
                      order={order}
                      onClick={() => navigate(`/orderer/order/${order.id}`)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* ── Leverte oppdrag ──────────────────────────────── */}
            {!loading && delivered.length > 0 && (
              <div style={styles.section}>
                <div style={styles.sectionHeader}>
                  <span style={styles.sectionTitle}>Leverte oppdrag</span>
                  {delivered.length > 4 && (
                    <button style={styles.seeAllBtn} onClick={() => navigate('/orderer/invoices')}>
                      Se faktura for alle {delivered.length} →
                    </button>
                  )}
                </div>
                <div style={styles.rows}>
                  {delivered.slice(0, 4).map(order => (
                    <OrderRow
                      key={order.id}
                      order={order}
                      onClick={() => navigate(`/orderer/order/${order.id}`)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* ── Empty state ───────────────────────────────────── */}
            {!loading && orders.length === 0 && (
              <EmptyState onNew={() => navigate('/orderer/create')} />
            )}

            {loading && <SkeletonDashboard />}

            {/* ── Stat modals ──────────────────────────────────────────────── */}
            {activeModal === 'active' && (
              <StatModal
                title="Aktive oppdrag"
                count={inFlight.length}
                onClose={() => setActiveModal(null)}
              >
                {inFlight.length === 0
                  ? <div style={styles.modalEmpty}>Ingen aktive oppdrag for øyeblikket</div>
                  : inFlight.map(order => (
                      <OrderRow
                        key={order.id}
                        order={order}
                        onClick={() => { setActiveModal(null); navigate(`/orderer/order/${order.id}`); }}
                      />
                    ))
                }
              </StatModal>
            )}

            {activeModal === 'action' && (
              <StatModal
                title="Venter på handling"
                count={needsAction.length}
                onClose={() => setActiveModal(null)}
              >
                {needsAction.length === 0
                  ? <div style={styles.modalEmpty}>Ingen oppdrag venter på handling</div>
                  : needsAction.map(order => (
                      <ActionItem
                        key={order.id}
                        order={order}
                        bidCount={bidCounts[order.id]}
                        onCopy={() => { setActiveModal(null); navigate(`/orderer/create?copy=${order.id}`); }}
                        onNavigate={() => { setActiveModal(null); navigate(`/orderer/order/${order.id}`); }}
                      />
                    ))
                }
              </StatModal>
            )}

            {activeModal === 'completed' && (
              <StatModal
                title="Fullførte oppdrag"
                count={delivered.length}
                onClose={() => setActiveModal(null)}
              >
                {delivered.length === 0
                  ? <div style={styles.modalEmpty}>Ingen fullførte oppdrag ennå</div>
                  : delivered.map(order => (
                      <OrderRow
                        key={order.id}
                        order={order}
                        onClick={() => { setActiveModal(null); navigate(`/orderer/order/${order.id}`); }}
                      />
                    ))
                }
              </StatModal>
            )}

          </div>
          <BroadcastModal />
        </div>
      } />
    </Routes>
    <OrdererTabBar />
    </div>
  );
}

// ── StatCard (nøkkeltall) ──────────────────────────────────────────────────
function StatCard({ value, label, dark, small, onClick }) {
  const long = String(value).length > 3 || small;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      style={{
        ...styles.statCard,
        ...(dark ? styles.statCardDark : {}),
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ ...styles.statValue, fontSize: long ? 21 : 30, color: dark ? COLORS.accent : COLORS.textDark }}>{value}</div>
      <div style={{ ...styles.statLabel, color: dark ? 'rgba(255,255,255,0.7)' : COLORS.textLight }}>{label}</div>
    </button>
  );
}

// ── ActionItem: oppdrag som trenger handling ────────────────────────────────
// Åpent anbud med utløpt frist beskrives etter virkelig situasjon: bud → velg vinner,
// ingen bud → endre og publiser på nytt. bidCount er undefined til antallet er lest.
function ActionItem({ order, bidCount, onNavigate, onCopy }) {
  const isBid    = order.status === 'open';
  const deadline = order.bidDeadline?.toDate?.();
  const noBids   = isBid && bidCount === 0;
  const hasBids  = isBid && bidCount > 0;
  const deadlineText = deadline ? ' · ' + deadline.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }).toUpperCase() : '';

  const cta = !isBid ? 'Bekreft'
    : noBids ? 'Endre og publiser på nytt'
    : hasBids ? `${bidCount} bud · Velg vinner`
    : 'Åpne';

  return (
    <div style={{ ...styles.actionItem, ...(noBids ? styles.actionItemStack : {}) }}>
      <button type="button" style={styles.actionInfo} onClick={onNavigate}>
        <div style={styles.rowTitle}>{orderTitle(order)}</div>
        <div style={styles.rowSub}>
          <OrderSupplierLogo order={order} />{placeName(order.pickupAddress)} → {placeName(order.deliveryAddress)}
        </div>
        {noBids ? (
          <>
            <div style={styles.expiredFlag}>Anbudsfrist utløpt, ingen bud</div>
            <div style={styles.rowSub}>Ingen sjåfører la inn bud. Endre oppdraget eller publiser på nytt.</div>
          </>
        ) : (
          <div style={styles.rowMono}>
            {isBid ? `ANBUDSFRIST PASSERT${deadlineText}` : 'VENTER PÅ DIN GODKJENNING'}
          </div>
        )}
      </button>
      <button type="button" style={styles.actionCta} onClick={noBids ? onCopy : onNavigate}>{cta}</button>
    </div>
  );
}

// ── OrderRow: flat rad med ikon, tittel, rute, tid og status ───────────────
function OrderRow({ order, onClick }) {
  const statusInfo = STATUS_LABEL[order.status] ?? { text: order.status, bg: COLORS.surfaceHover, color: COLORS.textLight, border: COLORS.border };
  const scheduled  = order.scheduledAt?.toDate?.();

  return (
    <button type="button" onClick={onClick} data-testid="order-card" style={styles.orderRow}>
      <span style={styles.rowIcon} aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 6h11v10H2zM13 9h4l3 3v4h-7" /><circle cx="6" cy="17.5" r="1.8" /><circle cx="16.5" cy="17.5" r="1.8" />
        </svg>
      </span>
      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <div style={styles.rowTitle}>{orderTitle(order)}</div>
        <div style={styles.rowSub}>
          <OrderSupplierLogo order={order} />{placeName(order.pickupAddress)} → {placeName(order.deliveryAddress)}
        </div>
        {scheduled && <div style={styles.rowMono}>{whenLabel(scheduled)}</div>}
      </div>
      <div style={styles.rowRight}>
        <span style={{ ...styles.statusBadge, background: statusInfo.bg, color: statusInfo.color }}>{statusInfo.text}</span>
        {order.fixedPrice
          ? <span style={styles.rowPrice}>{order.fixedPrice.toLocaleString('nb-NO')}</span>
          : <span style={styles.rowPriceMuted}>Anbud</span>}
      </div>
    </button>
  );
}

// ── EmptyState ─────────────────────────────────────────────────────────────
function EmptyState({ onNew }) {
  return (
    <div style={styles.emptyState}>
      <TruckEmpty title="Ingen oppdrag ennå" text="Opprett ditt første oppdrag for å komme i gang" />
      <button className="tippi-btn" style={styles.newBtn} onClick={onNew}>
        + Opprett oppdrag
      </button>
    </div>
  );
}

// ── SkeletonDashboard ──────────────────────────────────────────────────────
function SkeletonDashboard() {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 32 }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{ background: COLORS.white, borderRadius: 10, padding: '20px 18px', boxShadow: SHADOWS.sm }}>
            <div className="skeleton" style={{ height: 36, width: '50%', marginBottom: 10 }} />
            <div className="skeleton" style={{ height: 12, width: '80%' }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
        {[0,1,2].map(i => (
          <div key={i} style={{ background: COLORS.white, borderRadius: 10, padding: '16px 18px', boxShadow: SHADOWS.sm }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
              <div className="skeleton" style={{ height: 18, width: '36%' }} />
              <div className="skeleton" style={{ height: 18, width: '26%', borderRadius: 20 }} />
            </div>
            <div className="skeleton" style={{ height: 12, width: '72%', marginBottom: 8 }} />
            <div className="skeleton" style={{ height: 12, width: '60%', marginBottom: 14 }} />
            <div style={{ display: 'flex', gap: 12 }}>
              <div className="skeleton" style={{ height: 11, width: '28%' }} />
              <div className="skeleton" style={{ height: 11, width: '22%' }} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── StatModal ──────────────────────────────────────────────────────────────
function StatModal({ title, count, onClose, children }) {
  return (
    <>
      <div style={styles.modalBackdrop} onClick={onClose} />
      <div style={styles.modalPanel}>
        <div style={styles.modalHeader}>
          <span style={styles.modalTitle}>
            {title}
            {count != null && (
              <span style={styles.modalCount}>{count}</span>
            )}
          </span>
          <button style={styles.modalClose} onClick={onClose}>×</button>
        </div>
        <div style={styles.modalBody}>
          {children}
        </div>
      </div>
    </>
  );
}

const styles = {
  root:         { minHeight: '100vh', background: COLORS.bg, display: 'flex', flexDirection: 'column' },
  page:         { maxWidth: 720, margin: '0 auto', padding: '32px 20px', width: '100%', paddingTop: 'max(32px, env(safe-area-inset-top))' },

  // Header
  pageHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, paddingTop: 12 },
  greeting:     { fontFamily: FONTS.display, fontSize: 36, lineHeight: 1.05, fontWeight: 700, color: COLORS.textDark, margin: '2px 0 0' },
  dateStr:      { fontFamily: 'var(--font-mono)', fontSize: 11, color: COLORS.textLight, margin: 0, letterSpacing: '0.04em' },
  newBtn:       { padding: '12px 22px', borderRadius: 26, border: 'none', background: COLORS.accent, color: '#1a1a2e', fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap', marginTop: 4 },

  // Nøkkeltall
  statsGrid:    { display: 'flex', gap: 8, marginBottom: 20 },
  statCard:     { flex: 1, minWidth: 0, background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: '10px 10px 9px', textAlign: 'left', fontFamily: 'inherit' },
  statCardDark: { background: COLORS.brandPanel, border: 'none' },
  statValue:    { fontFamily: FONTS.display, fontWeight: 700, lineHeight: 1.1 },
  statLabel:    { fontSize: 10.5, lineHeight: 1.2, marginTop: 3 },

  // Trenger handling
  actionSection:{ marginBottom: 8 },
  actionHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  actionDot:    { width: 8, height: 8, borderRadius: '50%', background: COLORS.accent, flexShrink: 0 },
  actionTitle:  { fontFamily: FONTS.display, fontSize: 21, fontWeight: 700, color: COLORS.textDark },
  actionCount:  { background: COLORS.accent, color: '#1a1a2e', borderRadius: 12, padding: '1px 8px', fontSize: 12, fontWeight: 700 },
  actionList:   { display: 'flex', flexDirection: 'column' },
  actionItem:   { display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '12px 0', borderBottom: `1px solid ${COLORS.border}`, color: COLORS.textDark },
  actionItemStack:{ flexDirection: 'column', alignItems: 'stretch', gap: 10 },
  actionInfo:   { flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', color: COLORS.textDark },
  expiredFlag:  { fontSize: 13, fontWeight: 700, color: COLORS.warningText, marginTop: 4 },
  actionCta:    { background: COLORS.accent, color: '#1a1a2e', border: 'none', borderRadius: 16, padding: '7px 14px', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0, cursor: 'pointer', fontFamily: 'inherit' },

  // Seksjoner
  section:      { marginBottom: 12 },
  sectionHeader:{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '14px 0 0' },
  sectionTitle: { fontFamily: FONTS.display, fontSize: 21, fontWeight: 700, color: COLORS.textDark },
  seeAllBtn:    { background: 'none', border: 'none', color: COLORS.textDark, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 },

  // Rader
  rows:         { display: 'flex', flexDirection: 'column' },
  orderRow:     { display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '12px 0', background: 'none', border: 'none', borderBottom: `1px solid ${COLORS.border}`, cursor: 'pointer', fontFamily: 'inherit', color: COLORS.textDark },
  rowIcon:      { width: 44, height: 44, borderRadius: 12, background: '#efeadd', color: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  rowTitle:     { fontWeight: 700, fontSize: 15, color: COLORS.textDark },
  rowSub:       { fontSize: 13, color: COLORS.textMid, marginTop: 1 },
  rowMono:      { fontFamily: 'var(--font-mono)', fontSize: 10.5, color: COLORS.textLight, marginTop: 3, letterSpacing: '0.03em' },
  rowRight:     { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 },
  rowPrice:     { fontFamily: FONTS.display, fontWeight: 700, fontSize: 20, lineHeight: 1, color: COLORS.textDark },
  rowPriceMuted:{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 18, lineHeight: 1, color: COLORS.textLight },
  statusBadge:  { borderRadius: 20, padding: '4px 10px', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' },

  // Route lines
  routeAddr:    { fontSize: 13, color: COLORS.textDark, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 },

  deliveryRow:  { display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px' },
  deliveryTime: { fontSize: 12, fontWeight: 700, color: COLORS.textMid, width: 120, flexShrink: 0 },
  deliveryDot:  { width: 10, height: 10, borderRadius: '50%', background: COLORS.accent, flexShrink: 0 },
  deliveryLine: { display: 'none' },
  deliveryInfo: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 },

  // Empty
  emptyState:   { textAlign: 'center', padding: '64px 20px', color: COLORS.textMid },

  errorMsg:     { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 20 },

  // Modal
  modalBackdrop:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000 },
  modalPanel:       { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 1001, background: COLORS.white, borderRadius: 14, width: 'calc(100% - 32px)', maxWidth: 540, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: SHADOWS.lg },
  modalHeader:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 14px', borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0 },
  modalTitle:       { fontFamily: FONTS.display, fontSize: 20, fontWeight: 700, color: COLORS.textDark, display: 'flex', alignItems: 'center', gap: 10 },
  modalCount:       { background: COLORS.surfaceHover, color: COLORS.primary, borderRadius: 20, padding: '1px 10px', fontSize: 14, fontWeight: 700 },
  modalClose:       { background: 'none', border: 'none', fontSize: 24, color: COLORS.textLight, cursor: 'pointer', lineHeight: 1, padding: '0 4px' },
  modalBody:        { overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 },
  modalEmpty:       { textAlign: 'center', padding: '32px 0', color: COLORS.textLight, fontSize: 14 },
  modalInvoiceRow:  { background: COLORS.surfaceHover, borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer' },
  paidBadge:        { display: 'inline-block', background: COLORS.successBg, color: COLORS.successText, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, marginTop: 4 },
};
